import styles from "./style.postcss";

import React, { Component } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { bindActionCreators } from "redux";
import { push } from "react-router-redux";
import { Link, withRouter } from "react-router";
import { List } from "immutable";
import is from "is_js";
import ReactGA from "react-ga4";
import { testClass, connect, Tooltip, Icon, PageCard } from "omni-common-ui";
import CourseViewTable from "components/CourseViewTable";
import ExpansionButton from "components/ExpansionButton";
import { actions as progressReportsActions } from "data/ProgressReports";
import {
  StudentReportSummary,
  actions as studentReportSummariesActions,
} from "data/ProgressReports/StudentReportSummaries";

import { actions as GroupMembers } from "data/GroupMembers";
import ProgressReportStatus from "components/ProgressReportStatus";
import ExportAllHandler from "./ExportAllHandler";

const MARKING = "edit";
const EXPORT_ALL = "export-all";
const EMPTY_HYPHEN = "–";

const COLUMN_SCORE = "total-score";
const COLUMN_COMMENTS = "comments";
const COLUMN_STATUS = "report-status";
const COLUMN_REPORT = "unit-report";

export class ProgressReportListView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isExpandRemainingMember: true,
    };
  }

  componentDidMount() {
    this.props.router.setRouteLeaveHook(
      this.props.route,
      this.routerWillLeave.bind(this),
    );
  }

  routerWillLeave(nextLocation) {
    const {
      studentReportSummaries,
      currentProgressReportId,
      submitStudentReportSummaryTeacherPublish,
      buildRoute,
      pushRoute,
      groupId,
    } = this.props;
    if (!nextLocation) return;
    if (nextLocation.pathname.endsWith("/publish-all")) {
      const studentReportSummaryIds = studentReportSummaries
        .toList()
        .filter((srs) => srs.status === StudentReportSummary.Status.Ready)
        .map((srs) => srs.id);
      submitStudentReportSummaryTeacherPublish(
        currentProgressReportId,
        groupId,
        studentReportSummaryIds,
      );
      pushRoute(buildRoute({ mode: "" }));
    }
  }

  _findProgressTestResultsForStudent(progressTestResults, studentId) {
    return progressTestResults.find((result) => result.studentId === studentId);
  }

  _getTotalAsPercentageOrHyphen({ totalScore = {} }) {
    if (
      !is.object(totalScore) ||
      totalScore.isEmpty ||
      totalScore.isNotApplicable
    ) {
      return EMPTY_HYPHEN;
    }
    return `${totalScore.percentage || 0}%`;
  }

  _gaLinkClicked() {
    ReactGA.event({
      category: "Progress Reports",
      action: "Clicked link to individual report",
    });
  }

  _renderCell(member, column) {
    const { studentReportSummaries, progressTest } = this.props;
    const {
      studentGeneralInfo: { id: studentId },
    } = member;
    const progressTestResults = progressTest.data.studentProgressTestResults;

    const reportSummaryForStudent = studentReportSummaries.find(
      (report) => report.studentId === studentId,
    );

    if (!reportSummaryForStudent && column !== COLUMN_REPORT) {
      if (column === COLUMN_STATUS) {
        return (
          <div className={styles.ProgressReportListView_status}>
            {EMPTY_HYPHEN}
          </div>
        );
      }
      return EMPTY_HYPHEN;
    }

    const studentProgressTestResults = this._findProgressTestResultsForStudent(
      progressTestResults,
      member.studentGeneralInfo.id,
    );
    const progressReportLinkClassName = classnames(
      styles.ProgressReportLink,
      testClass("report-link-cell"),
    );

    switch (column) {
      case COLUMN_SCORE:
        return this._getTotalAsPercentageOrHyphen(studentProgressTestResults);
      case COLUMN_COMMENTS:
        return this._renderComments(reportSummaryForStudent.comments);
      case COLUMN_STATUS:
        return (
          <div className={styles.ProgressReportListView_status}>
            <ProgressReportStatus
              className={styles.ProgressReportListView_status_content}
              status={reportSummaryForStudent.status}
            />
          </div>
        );
      case COLUMN_REPORT:
        return (
          <Link
            className={progressReportLinkClassName}
            to={this.props.buildRoute(`./student/${studentId}`)}
            draggable={false}
            onClick={() => this._gaLinkClicked()}
          >
            Report{" "}
            <Icon id="arrow-right" className={styles.ProgressReportLink_icon} />
          </Link>
        );
      default:
        throw new Error(`Invalid column: ${column}`);
    }
  }

  _renderComments(comments) {
    if (!comments) {
      return (
        <span
          className={classnames(
            styles.ProgressReportListView_comment,
            styles.__empty,
          )}
        >
          {EMPTY_HYPHEN}
        </span>
      );
    }

    return (
      <Tooltip
        className={styles.ProgressReportListView_comment}
        text={comments}
      >
        <span className={styles.ProgressReportListView_comment_inner}>
          {comments}
        </span>
      </Tooltip>
    );
  }

  _renderExpandButton() {
    const { isExpandRemainingMember } = this.state;

    return (
      <ExpansionButton
        isExpand={isExpandRemainingMember}
        onExpansionButtonClick={() =>
          this.setState({ isExpandRemainingMember: !isExpandRemainingMember })
        }
        className={styles.ProgressReportListView_expand}
      />
    );
  }

  _renderURTable(members) {
    let tip = "";

    if (this.props.courseType.indexOf("SSv3") !== -1) {
      tip = (
        <Tooltip
          text="Total Score calculated from Can-do Statements"
          placement="right"
          trigger={["hover"]}
          className={styles.ProgressReportListView_cardHeader_tooltip}
        >
          <Icon id="question" />
        </Tooltip>
      );
    }

    return (
      <CourseViewTable students={members}>
        <CourseViewTable.Header>
          <CourseViewTable.Row>
            <CourseViewTable.Cell columnId={COLUMN_SCORE}>
              Total Score
              {tip}
            </CourseViewTable.Cell>
            <CourseViewTable.Cell columnId={COLUMN_COMMENTS}>
              Comments
            </CourseViewTable.Cell>
            <CourseViewTable.Cell columnId={COLUMN_STATUS}>
              Report Status
            </CourseViewTable.Cell>
            <CourseViewTable.Cell columnId={COLUMN_REPORT} />
          </CourseViewTable.Row>
        </CourseViewTable.Header>
        <CourseViewTable.Body>
          {(member, column) => this._renderCell(member, column)}
        </CourseViewTable.Body>
      </CourseViewTable>
    );
  }

  render() {
    const {
      members,
      studentReportSummaries,
      currentProgressReportId,
      languageCode,
      pushRoute,
      buildRoute,
    } = this.props;

    if (!List.isList(members) || !studentReportSummaries) {
      return null;
    }

    const isInExportAllRoute = ProgressReportListView.isInExportAllRoute(
      this.props,
    );

    // disregard reports that are for students not in the list of group members
    const memberStudentIds = members.map(
      (member) => member.studentGeneralInfo.id,
    );
    const exportableStudentReportSummaries = studentReportSummaries.filter(
      (report) => memberStudentIds.indexOf(report.studentId) !== -1,
    );

    let remainingMembers = [];
    if (this.state.isExpandRemainingMember) {
      remainingMembers = this.props.remainingMembers;
    }

    return (
      <div>
        <PageCard>
          <PageCard.Heading
            text="PT Session Members"
            className={styles.ProgressReportListView_cardHeader}
            titleClassName={styles.ProgressReportListView_cardHeader_title}
          >
            <Tooltip
              text="PT Session: Last session of every unit"
              placement="right"
              trigger={["hover"]}
              className={styles.ProgressReportListView_cardHeader_tooltip}
            >
              <Icon id="question" />
            </Tooltip>
          </PageCard.Heading>
          <PageCard.Content className={styles.ProgressReportListView_content}>
            {this._renderURTable(this.props.ptSessionMembers)}
          </PageCard.Content>
        </PageCard>
        <PageCard>
          <PageCard.Heading
            text="Remaining Session Members"
            className={styles.ProgressReportListView_cardHeader}
          />
          <PageCard.Content className={styles.ProgressReportListView_content}>
            {this._renderURTable(remainingMembers)}
          </PageCard.Content>
          {this._renderExpandButton()}
        </PageCard>
        {isInExportAllRoute ? (
          <ExportAllHandler
            studentReportSummaries={exportableStudentReportSummaries}
            progressReportId={currentProgressReportId}
            bestCaseReportCount={members.size}
            languageCode={languageCode}
            pushRoute={pushRoute}
            buildRoute={buildRoute}
          />
        ) : null}
      </div>
    );
  }
}

ProgressReportListView.isInMarkingRoute = (props) =>
  props.params && props.params.mode === MARKING;

ProgressReportListView.isInExportAllRoute = (props) =>
  props.params && props.params.mode === EXPORT_ALL;

ProgressReportListView.propTypes = {
  members: PropTypes.object.isRequired,
  ptSessionMembers: PropTypes.object.isRequired,
  remainingMembers: PropTypes.object.isRequired,
  currentProgressReportId: PropTypes.string.isRequired,
  progressReports: PropTypes.object.isRequired,
  progressReport: PropTypes.object,
  progressTest: PropTypes.object,
  studentReportSummaries: PropTypes.object,
  languageCode: PropTypes.string,
  courseType: PropTypes.string,
  fetchProgressReport: PropTypes.func.isRequired,
  submitStudentReportSummaryTeacherPublish: PropTypes.func.isRequired,
  params: PropTypes.object.isRequired,
  buildRoute: PropTypes.func,
  pushRoute: PropTypes.func,
  router: PropTypes.any.isRequired,
  routes: PropTypes.array.isRequired,
  route: PropTypes.any.isRequired,
  groupId: PropTypes.string,
};

function mapStateToProps(state, ownProps) {
  const { groupId } = ownProps.params;
  const group = state.get("rootReducer").get("groups").get(groupId).data;
  const progressReports = group.get("progressReports");
  const languageCode = group.get("languageCode");
  const courseTypeLevelCode = group.get("courseTypeLevelCode");
  const courseTypeCode = group.get("courseTypeCode");
  const courseType = courseTypeCode + courseTypeLevelCode;
  const currentProgressReportId = ownProps.params.progressReportId;
  if (!currentProgressReportId) return;
  const studentReportSummaries = state
    .get("rootReducer")
    .get("studentReportSummaries")
    .get(currentProgressReportId).data;
  const ptSessionStudentIds =
    studentReportSummaries &&
    studentReportSummaries
      .filter((studentReportSummary) => studentReportSummary.isPTSessionMember)
      .map((studentReportSummary) => studentReportSummary.studentId);
  const remainingStudentIds =
    studentReportSummaries &&
    studentReportSummaries
      .filter((studentReportSummary) => !studentReportSummary.isPTSessionMember)
      .map((studentReportSummary) => studentReportSummary.studentId);
  const members = GroupMembers.getMembersByGroup(state, groupId);
  const ptSessionMembers =
    ptSessionStudentIds &&
    members.filter((member) =>
      ptSessionStudentIds.includes(member.studentGeneralInfo.id),
    );
  const remainingMembers =
    remainingStudentIds &&
    members.filter((member) =>
      remainingStudentIds.includes(member.studentGeneralInfo.id),
    );
  const progressReport = progressReports.data.get(currentProgressReportId);
  const currentProgressTestId = progressReport.progressTestId;
  const progressTest = state
    .get("rootReducer")
    .get("progressTests")
    .get(currentProgressTestId);

  return {
    members,
    ptSessionMembers,
    remainingMembers,
    currentProgressReportId,
    progressReports,
    progressReport,
    progressTest,
    studentReportSummaries,
    groupId,
    languageCode,
    courseType,
  };
}

function mapDispatchToProps(dispatch) {
  return Object.assign(
    { pushRoute: (path) => dispatch(push(path)) },
    bindActionCreators(progressReportsActions, dispatch),
    bindActionCreators(studentReportSummariesActions, dispatch),
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(ProgressReportListView));



// WEBPACK FOOTER //
// ./containers/Group/ProgressReport/ProgressReportListView/component.jsx